import { useState } from "react";

const features = [
  {
    icon: "ri-leaf-line",
    title: "Mycelium Fermentation",
    desc: "Proprietary fermentation processes transform fungi biomass into high-density protein matrices with complete amino acid profiles — no animals required.",
  },
  {
    icon: "ri-flask-line",
    title: "Precision Nutrition",
    desc: "Engineered at the molecular level, Prime Roots proteins deliver 30g of complete protein per serving with superior bioavailability compared to conventional sources.",
  },
  {
    icon: "ri-recycle-line",
    title: "Carbon-Negative Production",
    desc: "Our closed-loop bioreactor systems consume CO₂ during cultivation, making every kilogram of protein a net positive for the planet.",
  },
  {
    icon: "ri-microscope-line",
    title: "Texture Engineering",
    desc: "Fungi-based protein naturally replicates the fibrous texture of meat, enabling seamless integration into existing food manufacturing pipelines.",
  },
];

export default function FoodTechSection() {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section id="food-tech" className="bg-[#f0faf7] py-28 relative overflow-hidden">
      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: "radial-gradient(circle, #00c8a820 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-6 mb-16">
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#0a2a1f] leading-tight">
            Sustainable<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00a882] to-[#00d4a8]">Food Tech</span>
          </h2>
          <p className="text-[#0a2a1f]/75 text-xl md:text-2xl leading-relaxed max-w-3xl">
            Reimagining the global protein supply through the power of fungi. Prime Roots leverages mycelium biology to create sustainable, nutritionally superior food ingredients at an industrial scale.
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left: Image + taste banner */}
          <div className="flex flex-col gap-6">
            <div className="relative w-full h-[480px] rounded-2xl overflow-hidden border border-[#00c8a8]/20">
              <img
                src="https://public.readdy.ai/ai/img_res/2b55137b754813c09f3d19217842bc2d.jpg"
                alt="Prime Roots mycelium protein cultivation"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#f0faf7]/60 via-transparent to-transparent"></div>

              {/* Metric overlay */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-md border border-[#00c8a8]/20 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[#0a2a1f]/80 text-sm uppercase tracking-widest mb-1 font-semibold">Protein Efficiency vs. Beef</div>
                      <div className="text-[#0a2a1f] font-black text-5xl">14x <span className="text-[#00a882]">More Efficient</span></div>
                    </div>
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#00c8a8]/10 border border-[#00c8a8]/30">
                      <i className="ri-bar-chart-2-line text-[#00a882] text-2xl"></i>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    {[
                      { label: "Water Saved", value: "95%" },
                      { label: "Land Use", value: "-99%" },
                      { label: "Carbon Neutral", value: "100%" },
                    ].map((m) => (
                      <div key={m.label} className="text-center">
                        <div className="text-[#00a882] font-bold text-lg">{m.value}</div>
                        <div className="text-[#0a2a1f]/40 text-xs mt-0.5">{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Taste callout — compact banner under photo */}
            <div className="rounded-xl bg-gradient-to-br from-[#065f46] to-[#047857] px-5 py-4">
              <h3 className="text-white font-black text-xl leading-tight mb-2 text-center">
                Taste That Needs No Compromise
              </h3>
              <p className="text-white/95 text-sm leading-relaxed text-center mb-3">
                Koji fermentation nails the flavor — umami depth, satisfying chew, rich savory finish. Blind taste tests show consistent preference over conventional animal proteins.
              </p>
              <div className="flex justify-center gap-3">
                {[
                  { stat: "50%+", label: "Prefer the taste" },
                  { stat: "100%", label: "Plant-based" },
                  { stat: "30g", label: "Protein/serving" },
                ].map((s) => (
                  <div key={s.label} className="bg-white/20 rounded-lg px-4 py-2 text-center border border-white/20">
                    <div className="text-[#6ee7b7] font-black text-base">{s.stat}</div>
                    <div className="text-white font-semibold text-xs">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Features */}
          <div className="flex flex-col gap-4">
            <div className="mb-4">
              <h3 className="text-[#0a2a1f] font-bold text-xl mb-2">Prime Roots Technology Platform</h3>
              <p className="text-[#0a2a1f]/80 text-sm leading-relaxed">
                Founded on the principle that the most advanced food technology already exists in nature, Prime Roots harnesses the extraordinary biological machinery of <strong className="text-[#0a2a1f]">Aspergillus oryzae</strong> — the same koji mold used in traditional fermentation for millennia — and scales it with modern bioengineering precision.
              </p>
            </div>

            {features.map((f, i) => (
              <button
                key={i}
                onClick={() => setActiveFeature(i)}
                className={`text-left w-full rounded-xl border p-6 transition-all duration-300 cursor-pointer bg-white ${
                  activeFeature === i
                    ? "border-[#00a882]/50 shadow-md shadow-[#00a882]/10"
                    : "border-[#0a2a1f]/10 hover:border-[#00a882]/30"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${activeFeature === i ? "bg-[#00a882]/15" : "bg-[#0a2a1f]/8"}`}>
                    <i className={`${f.icon} text-xl ${activeFeature === i ? "text-[#00a882]" : "text-[#0a2a1f]/70"}`}></i>
                  </div>
                  <div>
                    <div className={`font-bold text-base mb-1.5 ${activeFeature === i ? "text-[#0a2a1f]" : "text-[#0a2a1f]"}`}>{f.title}</div>
                    {activeFeature === i && (
                      <p className="text-[#0a2a1f]/75 text-sm leading-relaxed">{f.desc}</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Bottom product showcase */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            {
              name: "Koji Bacon",
              desc: "Crispy, smoky, and deeply savory — the caramelized umami flavor is indistinguishable from pork. Crafted entirely from mycelium.",
              img: "https://readdy.ai/api/search-image?query=beautifully%20plated%20artisan%20plant%20based%20bacon%20strips%20with%20rich%20caramel%20and%20mahogany%20tones%2C%20glistening%20smoky%20texture%20on%20bright%20white%20ceramic%20plate%2C%20vibrant%20professional%20food%20photography%20with%20warm%20golden%20natural%20lighting%2C%20fresh%20herbs%20garnish%2C%20appetizing%20and%20colorful%20minimal%20background&width=400&height=280&seq=foodprod_bright_01&orientation=landscape",
            },
            {
              name: "Koji Tuna",
              desc: "Delicate, clean ocean flavor with a satisfying flake — bright, fresh taste and omega-rich nutrition, no fishing required.",
              img: "https://readdy.ai/api/search-image?query=vibrant%20plant%20based%20tuna%20poke%20bowl%20with%20bright%20pink%20and%20coral%20colored%20flaky%20protein%2C%20topped%20with%20vivid%20green%20avocado%20slices%2C%20orange%20sesame%20seeds%2C%20fresh%20cucumber%20ribbons%20and%20colorful%20microgreens%2C%20bright%20airy%20food%20photography%20on%20white%20marble%20surface%2C%20appetizing%20and%20fresh&width=400&height=280&seq=foodprod_bright_02&orientation=landscape",
            },
            {
              name: "Koji Chicken",
              desc: "Juicy, tender, and full of savory depth — the fibrous texture and mild flavor make it a seamless swap in any dish. Zero antibiotics, pure taste.",
              img: "https://readdy.ai/api/search-image?query=golden%20crispy%20plant%20based%20chicken%20alternative%20with%20beautiful%20caramelized%20crust%2C%20sliced%20to%20reveal%20tender%20fibrous%20interior%2C%20served%20on%20bright%20white%20plate%20with%20vibrant%20green%20herb%20salad%20and%20lemon%20wedge%2C%20warm%20golden%20hour%20food%20photography%2C%20appetizing%20colorful%20presentation%20on%20clean%20background&width=400&height=280&seq=foodprod_bright_03&orientation=landscape",
            },
          ].map((p) => (
            <div key={p.name} className="rounded-xl border border-[#0a2a1f]/10 bg-white overflow-hidden group hover:border-[#00a882]/40 transition-all duration-300">
              <div className="w-full h-44 overflow-hidden">
                <img src={p.img} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-5 rounded-full bg-gradient-to-b from-[#00a882] to-[#00d4a8]"></div>
                  <div className="text-[#0a2a1f] font-black text-lg tracking-tight">{p.name}</div>
                </div>
                <div className="text-[#0a2a1f]/90 text-sm leading-relaxed">{p.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
